import { IPriceExplanationBenefit } from '@alao-frontend/core'
import type { ProviderModel } from '@alao-frontend/erp-services'
import { NetworkType } from '~/components/sections/coverage/types'

export type ValueOf<T> = T[keyof T]

/* eslint-disable camelcase */
export enum ProviderScoreType {
  INTERNET = 'internet',
  MOBILE = 'mobile',
  TV = 'tv',
  TIME_TO_INSTALL = 'time_to_install_score',
  CUSTOMER_SERVICE = 'customer_service_score',
  USER_EXPERIENCE = 'user_experience_score',
  CORRECT_INVOICES = 'correct_invoices_score',
}

export interface ProviderScore {
  name: ProviderScoreType,
  score: number,
}

export interface ProviderSeo {
  title: string,
  description: string,
  meta_description: string,
  meta_keywords: string,
  title_h1: string,
  json_ld_markup: Record<string, any> | null,
}

export type PortingProvider = {
  name: string,
  slug: string,
}

/**
 * @deprecated - use ProviderModel provided by @alao-frontend/erp-services package
 */
export interface Provider {
  available_networks: NetworkType[] | null,
  avg_score: number,
  feedback_set_count: number,
  home: boolean,
  logo: string,
  mobile: boolean,
  name: string,
  network: string,
  pk: number,
  score_by_service: ProviderScore[],
  site_url: string,
  slug: string,
  seo: null | ProviderSeo,
  porting_providers: PortingProvider[],
}

export type ExtendedProviderModel = ProviderModel & {
  title_h1: string | null,
  meta_keywords: string | null,
  reviews_count: number | null,
  rating: number | null,
  json_ld_markup: null | {
    name: string,
    description: string,
    '@type': string,
    '@context': string,
  },
  title_SEO: string | null,
  meta_description: string | null,
  description: string | null,
}

export enum AgeGroup {
  '18_29' = '18-29',
  '30_39' = '30-39',
  '40+' = '40+',
}

export interface Review {
  [key: string]: any,
}

export enum OfferCategoryNamesType {
  MOBILE_ABO_OFFER = 'Mobile',
  HOME_OFFER = 'Home',
  BUNDLE_OFFER = 'Bundle',
}

export type OfferCategoryType = keyof typeof OfferCategoryNamesType

export enum OfferCategoryIdsType {
  MOBILE_ABO_OFFER = 1,
  HOME_OFFER = 2,
  BUNDLE_OFFER = 3,
}

export interface OfferBusinessCommissionMetrics {
  fpromtrid: number,
  gtrhref: number,
  timetosale: number,
}

export enum OfferType {
  MOBILE_ABO_OFFER = 'mobile',
  HOME_OFFER = 'home',
  BUNDLE_OFFER = 'bundle',
}

export type OfferTypeKey = keyof typeof OfferType

// TODO: Try to get rid of it
export enum OfferTypeByShortName {
  mobile = 'MOBILE_ABO_OFFER',
  home = 'HOME_OFFER',
  bundle = 'BUNDLE_OFFER',
}

export enum ContractType {
  FIXED_CONTRACT = 'fixed_contract',
  PREPAID_CONTRACT = 'prepaid_contract',
}

// Used for analytics
export enum ContractTypes {
  fixed_contract = 'Postpaid',
  prepaid_contract = 'Prepaid',
}

export type ContractTypeKey = keyof typeof ContractTypes

export enum ProductVariants {
  BUNDLE = 'Bundle',
  INDIVIDUAL = 'Individual Plan',
}

export interface IGoogleRating {
  rating: number,
  reviews: number,
}

export interface CitiesResponse {
  cities: string[],
}

export interface StreetResponse {
  streets: string[],
}

export interface HouseResponse {
  houses: string[],
}

export enum Gender {
  MALE = 'male',
  FEMALE = 'female',
}

export enum BonusType {
  VOUCHER = 'voucher',
  WELCOME = 'welcome',
  CASHBACK = 'cashback',
}

export interface Bonus {
  type: BonusType,
  translateKey: string,
  value: number,
}

export interface OfferPriceExplanation {
  price: number,
  contractDuration: number,
  effectivePrice: number,
  savings: number,
  benefits: IPriceExplanationBenefit[],
}

export type OfferPriceExplanationFormula = Omit<OfferPriceExplanation, 'benefits'>

export interface AlaoBenefit {
  profit_title: string,
  profit_value: string,
  currency: string,
  explanation?: string,
  tag?: string | null,
}

export enum OfferTag {
  ALAO = 'alao',
  ALAO_PLUS = 'alao_plus',
  OFFER_OF_THE_WEEK = 'offer_of_the_week',
  ALAO_PLUS_EXCLUSIVE = 'alao_plus_exclusive',
}

/* eslint-enable camelcase */

export interface OfferMobileSort {
  value: string,
  label: string,
}

export type WhatsAppShare = {
  offerUrl: string,
  offerTitle?: string,
  offerPrice: number,
  discount: number,
  providerName: string,
}

export interface OfferBadgeRaw {
  title: string,
  title_de: string | null,
  title_fr: string | null,
  subtitle: string,
  subtitle_de: string | null,
  subtitle_fr: string | null,
  title_font_size: 18,
  subtitle_font_size: number,
  color_hex_code: string,
  background: string,
}
